/**
 * PATH: packages/email-hub/src/_internal/hooks/useIsNarrowViewport.ts
 * FILE_ID: EMAILHUB-INTERNAL-HOOK-NARROW-VIEWPORT-001
 * PURPOSE: Email-hub-specific review-width signal for surfaces that cannot
 *          safely sustain a list + reader split below a desktop budget.
 * WHY:     768–1023px tablets passed the old phone-only (<768) gate and
 *          rendered a list beside a crushed reader. Email needs an exclusive
 *          review mode through Tailwind's lg boundary.
 * RELATED: packages/ui/src/hooks/useBreakpoint.ts (shared breakpoints).
 * This intentionally differs from tenant-shared's phone-width hook; do not
 * reuse it outside Email Hub without reviewing the split-pane budget.
 */

import { useEffect, useState } from 'react'

const QUERY = '(max-width: 1023.98px)'

export function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return window.matchMedia(QUERY).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mql = window.matchMedia(QUERY)
    setIsNarrow(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [])

  return isNarrow
}
